// src/contexts/SignupJourneyContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';

import type { EducationStatus } from '@/lib/models/candidateProfile';

// Backend CreateCandidateProfileRequest structure
export interface CandidateProfilePayload {
    // Candidate metadata
    // Education is optional since #298 — see EducationStatus. Kept on the payload
    // because the profile page can still set it.
    university: string;
    education_status: EducationStatus;
    other_education?: string; // only when education_status === 'other'

    // Question 1: Hours per week
    min_hours_per_week: number;
    max_hours_per_week: number;

    // Question 2: When can you start?
    available_from_date: string;

    // Question 3: Location
    location: string;

    // Optional fields
    address?: string;
    postal_code?: string;
}

// User registration data (separate from candidate profile)
export interface UserRegistrationData {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'candidate' | 'employer' | 'admin';
}

interface SignupJourneyContextType {
    // User registration data
    userRegistration: Partial<UserRegistrationData>;
    updateUserRegistration: (field: keyof UserRegistrationData, value: any) => void;

    // Candidate profile data (optional, only for candidates)
    candidateProfile: Partial<CandidateProfilePayload>;
    updateCandidateProfile: (field: keyof CandidateProfilePayload, value: any) => void;

    // Combined submission
    getCombinedSignupPayload: () => {
        email: string;
        password: string;
        first_name: string;
        last_name: string;
        role: string;
        extra?: CandidateProfilePayload;
    } | null;

    // Reset
    resetData: () => void;
}

const SignupJourneyContext = createContext<SignupJourneyContextType | undefined>(undefined);

const initialUserRegistration: Partial<UserRegistrationData> = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'candidate'
};

const initialCandidateProfile: Partial<CandidateProfilePayload> = {
    university: '',
    education_status: undefined,
    other_education: '',
    min_hours_per_week: 10,
    max_hours_per_week: 20,
    available_from_date: '',
    location: '',
};

export const SignupJourneyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [userRegistration, setUserRegistration] = useState<Partial<UserRegistrationData>>(initialUserRegistration);
    const [candidateProfile, setCandidateProfile] = useState<Partial<CandidateProfilePayload>>(initialCandidateProfile);

    const updateUserRegistration = (field: keyof UserRegistrationData, value: any) => {
        setUserRegistration(prev => ({ ...prev, [field]: value }));
    };

    const updateCandidateProfile = (field: keyof CandidateProfilePayload, value: any) => {
        setCandidateProfile(prev => ({ ...prev, [field]: value }));
    };

    // ✅ Validates and combines user registration + candidate profile
    const getCombinedSignupPayload = () => {
        // Validate user registration fields
        if (!userRegistration.email || !userRegistration.password ||
            !userRegistration.first_name || !userRegistration.last_name ||
            !userRegistration.role) {
            console.error('Missing required user registration fields');
            return null;
        }

        const basePayload = {
            email: userRegistration.email,
            password: userRegistration.password,
            first_name: userRegistration.first_name,
            last_name: userRegistration.last_name,
            role: userRegistration.role
        };

        // Only include candidate profile for candidate role
        if (userRegistration.role === 'candidate') {
            // #298 — university and education_status are no longer required. This
            // check ran BEFORE the request was built and returned null, so a signup
            // that skipped education failed silently with a console line and no
            // user-visible reason. Once the education step was removed from the
            // form, leaving them here would have made candidate signup impossible.
            if (!candidateProfile.min_hours_per_week || !candidateProfile.max_hours_per_week ||
                !candidateProfile.available_from_date || !candidateProfile.location) {
                console.error('Missing required candidate profile fields');
                return null;
            }

            // "other" status requires a free-text description
            if (candidateProfile.education_status === 'other' && !candidateProfile.other_education?.trim()) {
                console.error('other_education is required when education_status is "other"');
                return null;
            }

            return {
                ...basePayload,
                extra: candidateProfile as CandidateProfilePayload
            };
        }

        // For non-candidate roles, no extra field
        return basePayload;
    };

    const resetData = () => {
        setUserRegistration(initialUserRegistration);
        setCandidateProfile(initialCandidateProfile);
    };

    return (
        <SignupJourneyContext.Provider value={{
            userRegistration,
            updateUserRegistration,
            candidateProfile,
            updateCandidateProfile,
            getCombinedSignupPayload,
            resetData
        }}>
            {children}
        </SignupJourneyContext.Provider>
    );
};

export const useSignupJourney = () => {
    const context = useContext(SignupJourneyContext);
    if (!context) {
        throw new Error('useSignupJourney must be used within SignupJourneyProvider');
    }
    return context;
};